import React from "react";
import SectionHeading from "@/components/SectionHeading";
import FeatureCard from "@/components/FeatureCard";
import CTABanner from "@/components/CTABanner";
import Reveal from "@/components/Reveal";
import Chevron from "@/components/Chevron";
import { useT } from "@/i18n/LanguageProvider";
import {
  MapPin, Route, ScanLine, BarChart3, Bell, Users, Layers, Zap, ShieldCheck,
  Brain, Truck, MapPinned, Plug, Tag, ClipboardCheck, Wallet, Globe, Webhook, Code2,
} from "lucide-react";

// Icon mappings per group/item index — order matches translations.features.groups
const ICON_GROUPS = [
  [MapPin, Users, Route, ScanLine, BarChart3, Bell],
  [Brain, Zap, MapPinned, ClipboardCheck],
  [Plug, Tag, Wallet, Globe, Webhook, Code2],
  [Layers, Truck, ShieldCheck],
];

export default function Features() {
  const t = useT();

  return (
    <>
      <section className="relative bg-delfi-dark text-white pt-36 pb-24 overflow-hidden" data-testid="features-hero">
        <div className="chevron-bg right-[-8%] top-[-18%] opacity-[0.08]" aria-hidden="true">›</div>
        <div className="absolute inset-0 grid-pattern opacity-40" aria-hidden="true" />
        <div className="delfi-container relative max-w-3xl">
          <div className="inline-flex items-center gap-2 text-delfi-red text-xs tracking-[0.2em] uppercase font-semibold">
            <Chevron size={14} /> {t.features.eyebrow}
          </div>
          <h1 className="headline-display text-white mt-4 text-[clamp(2.2rem,5vw,4rem)]" data-testid="features-title">
            {t.features.titleLead} <span className="text-delfi-red">{t.features.titleHighlight}</span>
          </h1>
          <p className="mt-6 text-white/75 text-lg leading-relaxed max-w-2xl">{t.features.description}</p>
        </div>
      </section>

      {(t.features.groups || []).map((group, idx) => (
        <section
          key={idx}
          className={`py-24 md:py-28 ${idx % 2 === 0 ? "bg-white" : "bg-delfi-cloud"}`}
          data-testid={`features-group-${idx}`}
        >
          <div className="delfi-container">
            <Reveal>
              <SectionHeading
                align="left"
                eyebrow={group.eyebrow}
                title={group.title}
                description={group.description}
              />
            </Reveal>
            <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {group.items.map((f, i) => {
                const Icon = (ICON_GROUPS[idx] || [])[i];
                return (
                  <FeatureCard
                    key={i}
                    icon={Icon}
                    title={f.title}
                    description={f.description}
                    delay={i * 70}
                  />
                );
              })}
            </div>
          </div>
        </section>
      ))}

      <CTABanner title={t.features.ctaTitle} subtitle={t.features.ctaSubtitle} />
    </>
  );
}
